import type { OnboardingStepProps } from '../OnboardingWizard';
import {
  ArrowBack,
  ArrowForward,
  CheckCircle,
  TrendingUp,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import React, { useContext, useState } from 'react';
import { UserContext } from '@/shared/contexts/UserContext';
import { useApi } from '@shared/services/api';

const PRESET_RATES = [75, 150, 250, 350];

const VALUE_PROPS = [
  'Matched with buyers based on your expertise and availability',
  'Your rate is shared only when you accept an engagement',
  "You're always in control — adjust or remove it any time",
];

export const HourlyRateStep: React.FC<OnboardingStepProps> = ({
  onNext,
  onBack,
  onSkip,
}) => {
  const api = useApi();
  const { user } = useContext(UserContext);
  const [rateValue, setRateValue] = useState<string>(user?.hourlyRate?.toString() ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rate = rateValue ? parseFloat(rateValue) : undefined;
  const isValid = !rateValue || (!isNaN(rate!) && rate! > 0);

  const handlePresetClick = (preset: number) => {
    setRateValue(preset.toString());
    setError(null);
  };

  const handleContinue = async () => {
    if (!isValid) {
      setError('Please enter a valid hourly rate (e.g. 150).');
      return;
    }

    if (rateValue && user?.id) {
      setIsSaving(true);
      setError(null);
      try {
        const response = await api.put('user', {
          id: user.id,
          hourlyRate: rate,
        });
        if (response?.error) {
          setError('Failed to save your rate. You can update it later in your profile.');
        }
      }
      catch {
        // Non-fatal — let them proceed
      }
      finally {
        setIsSaving(false);
      }
    }

    onNext();
  };

  return (
    <div className="onboarding-step" data-testid="hourly-rate-step">
      <div className="onboarding-step__content">

        {/* Hero icon + intro */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, mb: 3.5 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 4px 14px rgba(46,125,50,0.3)',
              }}
            >
              <TrendingUp sx={{ fontSize: 26, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5, lineHeight: 1.4 }}>
                Set your consulting rate
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                Kandir matches buyers with experts like you for paid engagements.
                We'll surface opportunities that fit your background — you only get contacted for deals worth your time.
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* Preset rate buttons */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1.25,
              color: 'text.secondary',
              fontWeight: 700,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              fontSize: '0.7rem',
            }}
          >
            Common rates
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {PRESET_RATES.map(preset => {
              const selected = rateValue === preset.toString();
              return (
                <Box
                  key={preset}
                  onClick={() => handlePresetClick(preset)}
                  sx={{
                    px: 2.25,
                    py: 0.85,
                    borderRadius: '10px',
                    border: '1.5px solid',
                    borderColor: selected ? '#2e7d32' : 'rgba(0,0,0,0.15)',
                    background: selected
                      ? 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)'
                      : 'white',
                    color: selected ? 'white' : 'text.primary',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'all 0.18s ease',
                    userSelect: 'none',
                    boxShadow: selected ? '0 3px 10px rgba(46,125,50,0.25)' : '0 1px 3px rgba(0,0,0,0.06)',
                    '&:hover': {
                      borderColor: '#2e7d32',
                      background: selected
                        ? 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)'
                        : 'rgba(46,125,50,0.05)',
                      color: selected ? 'white' : '#2e7d32',
                      transform: 'translateY(-1px)',
                      boxShadow: selected
                        ? '0 5px 14px rgba(46,125,50,0.3)'
                        : '0 3px 8px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  ${preset}<span style={{ fontWeight: 400, opacity: 0.8 }}>/hr</span>
                </Box>
              );
            })}
          </Box>
        </motion.div>

        {/* Rate input */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(46, 125, 50, 0.06) 0%, rgba(102, 187, 106, 0.04) 100%)',
              border: '1.5px solid rgba(46, 125, 50, 0.2)',
              borderRadius: '14px',
              px: 3,
              py: 2.5,
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                Your rate
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                Enter a custom amount or pick one above
              </Typography>
            </Box>
            <TextField
              value={rateValue}
              onChange={e => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setRateValue(val);
                setError(null);
              }}
              type="text"
              inputMode="numeric"
              size="medium"
              placeholder="150"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: '#2e7d32', fontWeight: 700, fontSize: '1.1rem' }}>$</Typography>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>/hr</Typography>
                  </InputAdornment>
                ),
              }}
              onKeyDown={e => e.key === 'Enter' && handleContinue()}
              sx={{
                width: 150,
                '& .MuiOutlinedInput-root': {
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  borderRadius: '10px',
                  '& fieldset': { borderColor: 'rgba(46,125,50,0.35)', borderWidth: 2 },
                  '&:hover fieldset': { borderColor: '#43a047' },
                  '&.Mui-focused fieldset': { borderColor: '#2e7d32', borderWidth: 2 },
                },
                '& input': {
                  textAlign: 'center',
                  py: 1.25,
                  letterSpacing: '0.01em',
                  color: '#1b5e20',
                },
              }}
              autoFocus
              error={!isValid}
            />
          </Box>
        </motion.div>

        {/* Value props */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Box
            sx={{
              background: 'rgba(0,0,0,0.02)',
              borderRadius: '12px',
              px: 2.5,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {VALUE_PROPS.map((text, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <CheckCircle sx={{ fontSize: 17, color: '#43a047', mt: '2px', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </motion.div>

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </div>

      <div className="onboarding-step__actions">
        {onBack
          ? (
              <Button onClick={onBack} startIcon={<ArrowBack />} color="inherit" disabled={isSaving}>
                Back
              </Button>
            )
          : <span />}
        <div className="onboarding-step__actions-right">
          <Button
            onClick={onSkip}
            variant="text"
            color="inherit"
            disabled={isSaving}
            className="onboarding-btn--skip"
          >
            Skip
          </Button>
          <Button
            onClick={handleContinue}
            variant="contained"
            endIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <ArrowForward />}
            className="onboarding-btn--cta"
            disabled={isSaving || !isValid}
          >
            {isSaving ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
};
